import React, { useRef, useEffect } from 'react';
import { Box, useTheme } from '@mui/material';

interface VoltageLifetimeChartProps {
  selectedVoltage?: number; // Voltage to highlight (default 2.13V)
  selectedSeries?: 'ELDC-HVR' | 'ELDC-R'; // Series to highlight
}

const VoltageLifetimeChart: React.FC<VoltageLifetimeChartProps> = ({
  selectedVoltage = 2.13,
  selectedSeries = 'ELDC-HVR'
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const theme = useTheme();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // High-DPI canvas scaling for crisp text
    const dpr = window.devicePixelRatio || 1;
    const displayWidth = 600;
    const displayHeight = 480;

    // Set actual canvas size (accounting for device pixel ratio)
    canvas.width = displayWidth * dpr;
    canvas.height = displayHeight * dpr;

    // Scale context to match device pixel ratio
    ctx.scale(dpr, dpr);

    // Clear canvas
    ctx.clearRect(0, 0, displayWidth, displayHeight);
    ctx.fillStyle = theme.palette.mode === 'dark' ? '#1a1a2e' : '#ffffff';
    ctx.fillRect(0, 0, displayWidth, displayHeight);

    const padding = 80;
    const paddingBottom = 140; // Extra space for legend at bottom
    const chartWidth = displayWidth - padding * 2;
    const chartHeight = displayHeight - padding - paddingBottom;

    // Data for Serie 235 ELDC-HVR (T_0 = 65°C, U_C0N1 = 3.0V, U_1C01 = 0.2V)
    // Format: [lifetime_hours, voltage]
    const seriesHVR: [number, number][] = [
      [1000, 3.0],
      [2000, 2.9],
      [5000, 2.7],
      [10000, 2.5],
      [20000, 2.3],
      [30000, 2.1]
    ];

    // Data for Serie 225 ELDC-R (T_0 = 65°C, U_C0N1 = 2.7V, U_1C01 = 0.2V)
    const seriesR: [number, number][] = [
      [1000, 2.7],
      [2000, 2.65],
      [5000, 2.55],
      [10000, 2.4],
      [20000, 2.2],
      [30000, 2.0]
    ];

    // Logarithmic scale for x-axis (lifetime)
    const logScale = (value: number, min: number, max: number): number => {
      const logMin = Math.log10(min);
      const logMax = Math.log10(max);
      const logValue = Math.log10(value);
      return (logValue - logMin) / (logMax - logMin);
    };

    const lifetimeMin = 1000;
    const lifetimeMax = 30000;
    const voltageMin = 2.0;
    const voltageMax = 3.0;

    const xScale = (lifetime: number) => {
      return padding + logScale(lifetime, lifetimeMin, lifetimeMax) * chartWidth;
    };

    const yScale = (voltage: number) => {
      return padding + chartHeight - ((voltage - voltageMin) / (voltageMax - voltageMin)) * chartHeight;
    };

    // Draw grid
    ctx.strokeStyle = theme.palette.mode === 'dark' ? '#333' : '#e8e8e8';
    ctx.lineWidth = 1;

    // Horizontal grid lines (voltage)
    const voltageSteps = [2.0, 2.2, 2.4, 2.6, 2.8, 3.0];
    voltageSteps.forEach(voltage => {
      const y = yScale(voltage);
      ctx.beginPath();
      ctx.moveTo(padding, y);
      ctx.lineTo(padding + chartWidth, y);
      ctx.stroke();

      // Y-axis labels
      ctx.fillStyle = theme.palette.mode === 'dark' ? '#fff' : '#000';
      ctx.font = '11px Arial';
      ctx.textAlign = 'right';
      ctx.fillText(voltage.toFixed(1), padding - 10, y + 4);
    });

    // Vertical grid lines (logarithmic lifetime)
    const lifetimeSteps = [1000, 2000, 5000, 10000, 20000, 30000];
    lifetimeSteps.forEach(lifetime => {
      const x = xScale(lifetime);
      ctx.beginPath();
      ctx.moveTo(x, padding);
      ctx.lineTo(x, padding + chartHeight);
      ctx.stroke();

      // X-axis labels
      ctx.font = '11px Arial';
      ctx.textAlign = 'center';
      const label = lifetime >= 10000 ? `${lifetime / 1000}k` : `${lifetime}`;
      ctx.fillText(label, x, padding + chartHeight + 20);
    });

    // Draw axes
    ctx.strokeStyle = theme.palette.mode === 'dark' ? '#fff' : '#000';
    ctx.lineWidth = 2;

    // Y-axis
    ctx.beginPath();
    ctx.moveTo(padding, padding);
    ctx.lineTo(padding, padding + chartHeight);
    ctx.stroke();

    // X-axis
    ctx.beginPath();
    ctx.moveTo(padding, padding + chartHeight);
    ctx.lineTo(padding + chartWidth, padding + chartHeight);
    ctx.stroke();

    // Draw Serie 235 ELDC-HVR (blue)
    ctx.strokeStyle = '#2563eb';
    ctx.lineWidth = 3;
    ctx.beginPath();

    seriesHVR.forEach(([lifetime, voltage], i) => {
      const x = xScale(lifetime);
      const y = yScale(voltage);

      if (i === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    });

    ctx.stroke();

    // Draw Serie 225 ELDC-R (orange)
    ctx.strokeStyle = '#f97316';
    ctx.lineWidth = 3;
    ctx.beginPath();

    seriesR.forEach(([lifetime, voltage], i) => {
      const x = xScale(lifetime);
      const y = yScale(voltage);

      if (i === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    });

    ctx.stroke();

    // Find selected voltage on the curves
    if (selectedVoltage >= voltageMin && selectedVoltage <= voltageMax) {
      // Interpolate lifetime for selected voltage
      const interpolateLifetime = (series: [number, number][], targetVoltage: number): number => {
        for (let i = 0; i < series.length - 1; i++) {
          const [life1, volt1] = series[i];
          const [life2, volt2] = series[i + 1];

          if ((volt1 >= targetVoltage && targetVoltage >= volt2) ||
              (volt1 <= targetVoltage && targetVoltage <= volt2)) {
            // Linear interpolation on log scale
            const t = (targetVoltage - volt1) / (volt2 - volt1);
            const logLife1 = Math.log10(life1);
            const logLife2 = Math.log10(life2);
            const logLifetime = logLife1 + t * (logLife2 - logLife1);
            return Math.pow(10, logLifetime);
          }
        }
        return series[series.length - 1][0]; // Return last value if not found
      };

      const lifetimeHVR = interpolateLifetime(seriesHVR, selectedVoltage);
      const lifetimeR = interpolateLifetime(seriesR, selectedVoltage);

      const selectedLifetime = selectedSeries === 'ELDC-HVR' ? lifetimeHVR : lifetimeR;
      const selectedX = xScale(selectedLifetime);
      const selectedY = yScale(selectedVoltage);

      // Draw selection indicator
      ctx.strokeStyle = '#dc2626';
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);

      // Vertical line
      ctx.beginPath();
      ctx.moveTo(selectedX, selectedY);
      ctx.lineTo(selectedX, padding + chartHeight);
      ctx.stroke();

      // Horizontal line
      ctx.beginPath();
      ctx.moveTo(selectedX, selectedY);
      ctx.lineTo(padding, selectedY);
      ctx.stroke();

      ctx.setLineDash([]);

      // Draw point
      ctx.fillStyle = '#dc2626';
      ctx.beginPath();
      ctx.arc(selectedX, selectedY, 6, 0, 2 * Math.PI);
      ctx.fill();

      // Label box - position it to avoid overlap with chart
      // Check if point is in left or right half to position label appropriately
      const isLeftHalf = selectedX < (padding + chartWidth / 2);
      const labelWidth = 110;
      const labelHeight = 28;
      const labelX = isLeftHalf ? selectedX + 15 : selectedX - labelWidth - 15;
      const labelY = Math.max(padding + 10, Math.min(selectedY - labelHeight / 2, padding + chartHeight - labelHeight - 10));

      ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
      ctx.strokeStyle = '#dc2626';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.roundRect(labelX, labelY, labelWidth, labelHeight, 4);
      ctx.fill();
      ctx.stroke();

      ctx.fillStyle = '#000';
      ctx.font = 'bold 10px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('Selected Voltage', labelX + labelWidth / 2, labelY + 11);
      ctx.font = 'bold 12px Arial';
      ctx.fillText(`${selectedVoltage.toFixed(2)} V`, labelX + labelWidth / 2, labelY + 23);
    }

    // Legend - horizontal at bottom with two columns
    const legendY = padding + chartHeight + 65;
    const legendStartX = padding;
    const legendColumnWidth = 300;

    const textColor = theme.palette.mode === 'dark' ? '#fff' : '#000';
    const detailColor = theme.palette.mode === 'dark' ? '#aaa' : '#666';

    // Serie 235 ELDC-HVR (left column)
    let currentX = legendStartX;
    
    // Line indicator
    ctx.strokeStyle = '#2563eb';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(currentX, legendY);
    ctx.lineTo(currentX + 30, legendY);
    ctx.stroke();

    // Label
    ctx.font = 'bold 12px Arial';
    ctx.textAlign = 'left';
    ctx.fillStyle = textColor;
    ctx.fillText('Serie 235 ELDC-HVR', currentX + 35, legendY + 4);
    
    // Details line 1
    ctx.font = '9px Arial';
    ctx.fillStyle = detailColor;
    ctx.fillText('T_0 = 65°C, U_C0N1 = 3.0V', currentX + 35, legendY + 18);
    
    // Details line 2
    ctx.fillText('U_1C01 = 0.2V', currentX + 35, legendY + 30);

    // Serie 225 ELDC-R (right column)
    currentX = legendStartX + legendColumnWidth;

    // Line indicator
    ctx.strokeStyle = '#f97316';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(currentX, legendY);
    ctx.lineTo(currentX + 30, legendY);
    ctx.stroke();

    // Label
    ctx.font = 'bold 12px Arial';
    ctx.fillStyle = textColor;
    ctx.fillText('Serie 225 ELDC-R', currentX + 35, legendY + 4);
    
    // Details line 1
    ctx.font = '9px Arial';
    ctx.fillStyle = detailColor;
    ctx.fillText('T_0 = 65°C, U_C0N1 = 2.7V', currentX + 35, legendY + 18);
    
    // Details line 2
    ctx.fillText('U_1C01 = 0.2V', currentX + 35, legendY + 30);

    // Axis labels
    ctx.fillStyle = theme.palette.mode === 'dark' ? '#fff' : '#000';
    ctx.font = 'bold 14px Arial';
    ctx.textAlign = 'center';

    // X-axis label
    ctx.fillText('Estimated Lifetime t_est [h]', displayWidth / 2, padding + chartHeight + 40);

    // Y-axis label (rotated)
    ctx.save();
    ctx.translate(20, displayHeight / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.fillText('Cell Voltage U_C0,max [V]', 0, 0);
    ctx.restore();

    // Title
    ctx.font = 'bold 16px Arial';
    ctx.fillText('Capacitor Voltage vs. Lifetime', displayWidth / 2, 30);

  }, [selectedVoltage, selectedSeries, theme]);

  return (
    <Box sx={{ width: '100%' }}>
      <canvas
        ref={canvasRef}
        style={{
          width: '100%',
          height: 'auto',
          display: 'block',
          borderRadius: '8px'
        }}
      />
    </Box>
  );
};

export default VoltageLifetimeChart;
